import {
  BatchConversionError,
  CustomAttributeDefinition,
  CustomAttributeDefinitionAttributableTypeEnum,
  CustomAttributeDefinitionsApiInterface,
} from "suus-api"
import { customAttributeDefinitionsApi } from "store/api"

/**
 *  ERROR TYPES
 */

export type CustomAttributeDefinitionUpdateError = {
  attributable: string
  name: string
  value: string
  error: string
}

function parseUpdateErrors(
  error: BatchConversionError
): CustomAttributeDefinitionUpdateError[] {
  const details = error.details
  return details.map((error) => ({
    attributable: error.attributable,
    name: error.name,
    value: error.value,
    error: error.error,
  }))
}

export class LockedException extends Error {
  constructor(message: string) {
    super(message)
    this.name = "LockedException"
    Object.setPrototypeOf(this, LockedException.prototype)
  }
}

/**
 * CLIENT
 * ----------------
 * Client Functions can throw errors, for example when the API is not available.
 * The Components that use the Client should catch these errors and handle them.
 */
let currentAttributes: CustomAttributeDefinition[] = []
const currentContext: { client_id: number | null; level_id: number | null } = {
  client_id: null,
  level_id: null,
}
let processingFetch: Promise<CustomAttributeDefinition[]> | null = null
let api: CustomAttributeDefinitionsApiInterface = customAttributeDefinitionsApi

function overrideCustomAttributesApi(
  newApi: CustomAttributeDefinitionsApiInterface
) {
  api = newApi
}

const CustomAttributesClient = {
  loadContext: async (
    client_id: number | null,
    level_id: number | null
  ): Promise<CustomAttributeDefinition[]> => {
    currentContext.client_id = client_id
    currentContext.level_id = level_id
    try {
      // Save the Promise, so fetch methods can wait for it.
      processingFetch = api
        .getCustomAttributeDefinitions(
          client_id?.toString(),
          level_id?.toString()
        )
        .then((response) => response.data)

      currentAttributes = await processingFetch
      return currentAttributes
    } finally {
      processingFetch = null
    }
  },
  fetchPersonalAttributes: async (): Promise<CustomAttributeDefinition[]> => {
    const filter = (attrs: CustomAttributeDefinition[]) =>
      attrs.filter((attr) => attr.attributable_type === "PersonalRecord")
    return processingFetch
      ? processingFetch.then(filter)
      : filter(currentAttributes)
  },
  fetchClubAttributes: async (): Promise<CustomAttributeDefinition[]> => {
    const filter = (attrs: CustomAttributeDefinition[]) =>
      attrs.filter((attr) => attr.attributable_type === "ClubRecord")
    return processingFetch
      ? processingFetch.then(filter)
      : filter(currentAttributes)
  },
  create: async (
    customAttributeDefinition: CustomAttributeDefinition
  ): Promise<CustomAttributeDefinition> => {
    const response = await api.createCustomAttributeDefinition(
      currentContext.client_id?.toString(),
      currentContext.level_id?.toString(),
      customAttributeDefinition
    )
    currentAttributes.push(response.data)
    return response.data
  },
  update: async (
    customAttributeDefinition: CustomAttributeDefinition
  ): Promise<
    CustomAttributeDefinition | CustomAttributeDefinitionUpdateError[]
  > => {
    try {
      const response = await api.updateCustomAttributeDefinition(
        customAttributeDefinition.id.toString(),
        {
          ...customAttributeDefinition,
          send_to_front: false,
          send_to_back: false,
        }
      )
      const index = currentAttributes.findIndex(
        (attr) => attr.id === customAttributeDefinition.id
      )
      currentAttributes[index] = response.data
      return response.data
    } catch (error) {
      const _error = error as any
      if (_error.response.status === 424) {
        return parseUpdateErrors(_error.response.data as BatchConversionError)
      }
      if (_error.response.status === 423) {
        throw new LockedException(_error.response.data)
      }
      throw error
    }
  },
  delete: async (id: number) => {
    await api.deleteCustomAttributeDefinition(id.toString())
    currentAttributes = currentAttributes.filter((attr) => attr.id !== id)
  },
  batchUpdate: async (id: number, file: File) => {
    return api.batchUpdateCustomAttributeValues(id.toString(), file)
  },
  move: async (
    id: number,
    attributable_type: CustomAttributeDefinitionAttributableTypeEnum
  ): Promise<CustomAttributeDefinition> => {
    try {
      const response = await api.updateCustomAttributeDefinition(
        id.toString(),
        {
          ...currentAttributes.find((attr) => attr.id === id),
          attributable_type,
          send_to_front: false,
          send_to_back: false,
        }
      )
      const index = currentAttributes.findIndex((attr) => attr.id === id)
      currentAttributes[index] = response.data
      return response.data
    } catch (error: any) {
      if (error.response.status === 423) {
        throw new LockedException(error.response.data)
      }
      throw error
    }
  },
  /**
   * Takes the ID of the attribute that should be moved and the ID of the target attribute (the attribute which place should be taken by the moved attribute).
   * Returns a list of all attributes in the new order.
   * If the moved and target attribute are both PersonalRecords-Attributes only PersonalRecords-Attributes are returned.
   * If the moved and target attribute are both ClubRecords-Attributes only ClubRecords-Attributes are returned.
   * If the moved and target attribute are of different types all attributes are returned.
   */
  reorder: async (
    movedId: number,
    targetId: number
  ): Promise<CustomAttributeDefinition[]> => {
    try {
      const moved = currentAttributes.find((attr) => attr.id === movedId)
      const target = currentAttributes.find((attr) => attr.id === targetId)
      const response = await api.updateCustomAttributeDefinition(
        movedId.toString(),
        {
          ...moved,
          index: target.index,
          send_to_front: false,
          send_to_back: false,
        }
      )
      const updatedMoved = response.data
      const movedIndexArray = currentAttributes.indexOf(moved)
      const targetIndexArray = currentAttributes.indexOf(target)
      currentAttributes.splice(movedIndexArray, 1)
      currentAttributes.splice(targetIndexArray, 0, updatedMoved)
      if (moved.attributable_type === target.attributable_type) {
        return currentAttributes.filter(
          (attr) => attr.attributable_type === moved.attributable_type
        )
      }
      return currentAttributes
    } catch (error: any) {
      if (error.response.status === 423) {
        throw new LockedException(error.response.data)
      }
      throw error
    }
  },
  fetchProgress: async (
    ids: number[]
  ): Promise<{ update_in_progress: boolean; id: number }[]> => {
    return Promise.all(
      ids.map((id) => api.getCustomAttributeDefinition(id.toString()))
    ).then((responses) => {
      responses.forEach((response) => {
        const index = currentAttributes.findIndex(
          (attr) => attr.id === response.data.id
        )
        currentAttributes[index] = response.data
      })
      return responses.map((response) => ({
        update_in_progress: response.data.update_in_progress,
        id: response.data.id,
      }))
    })
  },
}

export { CustomAttributesClient, overrideCustomAttributesApi }
